import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { asJxgPoint, type ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { isElement } from '../core/element.utils';
import {
  attachReconfigure,
  bindLabel,
  createLineLabelContext,
  createPointLabelContext,
  createPolygonLabelContext,
} from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { createPoint } from '../point/point.create';
import type { ElementTheme, LineStyle, PointStyle, ThemeColorWithShade } from '../theme/theme.model';
import type { PolygonArgs, PolygonResult } from './polygon.model';
import { generateVertexNames } from './polygon.model';

/**
 * Resolve vertex style with proper propagation order:
 * 1. theme.point
 * 2. theme.polygon.vertex (color defaults to parent polygon's strokeColor)
 * 3. style.vertex
 * 4. style.vertexPerIndex[index]
 */
function resolveVertexStyle(
  theme: ElementTheme,
  parentColor: ThemeColorWithShade,
  styleVertex?: PointStyle,
  perIndex?: PointStyle,
): PointStyle {
  let result: PointStyle = { ...theme.point };
  result = deepMerge(result, { ...theme.polygon.vertex, color: theme.polygon.vertex.color ?? parentColor });
  result = deepMerge(result, styleVertex);
  result = deepMerge(result, perIndex);
  return result;
}

/**
 * Apply resolved vertex style to a pre-created JSXGraph point element
 * so it gets proper theme colors instead of JSXGraph defaults.
 */
function applyPointStyle(
  element: GeometryElement,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  vertexStyle: PointStyle,
): void {
  const fillColor = resolveColor(themeService, vertexStyle.color ?? theme.point.color);
  const strokeColor = vertexStyle.strokeColor ? resolveColor(themeService, vertexStyle.strokeColor) : 'transparent';

  const typed = element as unknown as { setAttribute: (a: Record<string, unknown>) => void };
  typed.setAttribute({
    size: vertexStyle.size ?? theme.point.size,
    face: vertexStyle.face ?? theme.point.face,
    fillColor,
    strokeColor,
    fillOpacity: vertexStyle.fillOpacity ?? theme.point.fillOpacity,
    strokeWidth: vertexStyle.strokeColor ? vertexStyle.strokeWidth ?? 1 : 0,
    fixed: vertexStyle.fixed ?? true,
    visible: vertexStyle.visible ?? true,
  });
}

/**
 * Resolve border style with proper propagation order:
 * 1. theme.line
 * 2. theme.polygon.border
 * 3. style.border
 * 4. style.borderPerEdge[index]
 */
function resolveBorderStyle(theme: ElementTheme, styleBorder?: LineStyle, perEdge?: LineStyle): LineStyle {
  let result: LineStyle = { ...theme.line };
  result = deepMerge(result, theme.polygon.border);
  result = deepMerge(result, styleBorder);
  result = deepMerge(result, perEdge);
  return result;
}

/**
 * Build JSXGraph polygon attributes from style configuration.
 */
function buildPolygonAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['polygon'],
  styleOverrides?: PolygonArgs['style'],
): Record<string, unknown> {
  const fillColor = resolveColor(themeService, styleOverrides?.fillColor ?? baseStyle.fillColor);
  const strokeColor = resolveColor(themeService, styleOverrides?.strokeColor ?? baseStyle.strokeColor);

  return {
    fillColor,
    fillOpacity: styleOverrides?.fillOpacity ?? baseStyle.fillOpacity,
    strokeColor,
    strokeWidth: styleOverrides?.strokeWidth ?? baseStyle.strokeWidth,
    visible: styleOverrides?.visible ?? true,
    withLabel: false,
    hasInnerPoints: false,
    fixed: true,
    highlight: false,
    // Vertex and border styles are handled through nested attributes
    vertices: { visible: false }, // We create vertices separately
    borders: { visible: false }, // We'll handle borders separately
  };
}

/**
 * Create a polygon element with vertices, borders, and optional labels.
 * Labels are now configured via the style property:
 * - style.label: Label for the polygon itself (centered)
 * - style.vertex.label: Default label for all vertices
 * - style.vertexPerIndex[i].label: Per-vertex label overrides
 *
 * Style propagation for vertices:
 * 1. theme.point (base point defaults)
 * 2. theme.polygon.vertex (polygon-specific vertex defaults)
 * 3. style.vertex (user-provided vertex defaults)
 * 4. style.vertexPerIndex[i] (per-vertex overrides)
 *
 * Style propagation for borders:
 * 1. theme.line (base line defaults)
 * 2. theme.polygon.border (polygon-specific border defaults)
 * 3. style.border (user-provided border defaults)
 * 4. style.borderPerEdge[i] (per-edge overrides)
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Polygon creation arguments
 * @returns Polygon result with elements and optional labels
 */
export function createPolygon(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: PolygonArgs,
): PolygonResult {
  const { board } = ctx;
  const { vertices: inputVertices, name = '', style, vertexNames } = args;

  const names = vertexNames ?? generateVertexNames(inputVertices.length);

  // Parent color for vertices: polygon's strokeColor (so vertices match the polygon border)
  const parentColor = style?.strokeColor ?? theme.polygon.strokeColor;

  // Resolve or create vertices
  const vertices: GeometryElement[] = [];

  for (let i = 0; i < inputVertices.length; i++) {
    const input = inputVertices[i];

    if (isElement(input)) {
      // Apply resolved vertex style to pre-created elements so they get
      // proper theme colors instead of JSXGraph defaults (e.g. orange).
      const vertexStyle = resolveVertexStyle(theme, parentColor, style?.vertex, style?.vertexPerIndex?.[i]);
      applyPointStyle(input, themeService, theme, vertexStyle);
      vertices.push(input);
    } else if (typeof input === 'string') {
      const found = board.elementsByName[input];
      if (!found) {
        throw new Error(`Point "${input}" not found on board`);
      }
      vertices.push(found as GeometryElement);
    } else {
      // Create new point from coords — suppress label (polygon manages vertex labels)
      const vertexStyle = resolveVertexStyle(theme, parentColor, style?.vertex, style?.vertexPerIndex?.[i]);
      const result = createPoint(ctx, themeService, theme, {
        coords: input,
        name: names[i],
        style: { ...vertexStyle, label: false },
      });
      vertices.push(result.point);
    }
  }

  // Build polygon attributes
  const polygonAttrs = buildPolygonAttributes(themeService, theme.polygon, style);

  // Create the polygon
  const polygon = board.create('polygon', vertices, polygonAttrs) as GeometryElement;

  // Get the borders created by the polygon
  const typedPolygon = polygon as unknown as { borders: GeometryElement[] };
  const borders = typedPolygon.borders ?? [];

  // Apply border styles
  for (let i = 0; i < borders.length; i++) {
    const borderStyle = resolveBorderStyle(theme, style?.border, style?.borderPerEdge?.[i]);
    const borderAttrs: Record<string, unknown> = {
      strokeColor: resolveColor(themeService, borderStyle.color),
      strokeWidth: borderStyle.strokeWidth,
      dash: borderStyle.dash,
      visible: borderStyle.visible ?? true,
      fixed: true,
      highlight: false,
    };

    const border = borders[i] as unknown as { setAttribute: (attrs: Record<string, unknown>) => void };
    border.setAttribute(borderAttrs);
  }

  const helpers: GeometryElement[] = [];

  // Determine color for labels (strip shade — labels always use shade 500)
  const labelColor = parseThemeColor(style?.fillColor ?? 'neutral').name;

  // Always create vertex labels — visibility is controlled via config
  const vertexLabels: Array<import('../label/label.bind').BoundLabel> = [];
  for (let i = 0; i < vertices.length; i++) {
    const vertexLabelConfig = style?.vertexPerIndex?.[i]?.label ?? style?.vertex?.label;

    const resolvedConfig = resolveLabelConfig(vertexLabelConfig, {
      color: labelColor,
      precision: ctx.defaultPrecision,
    });
    const boundLabel = bindLabel({
      ctx,
      themeService,
      element: vertices[i],
      config: resolvedConfig,
      contextProvider: createPointLabelContext(names[i], vertices[i]),
    });
    attachReconfigure(vertices[i], boundLabel);
    vertexLabels.push(boundLabel);
  }

  // Always create border labels — visibility is controlled via config
  // Determine polygon winding so edge labels are placed on the outside.
  // Signed area > 0 → CCW, < 0 → CW (in math coordinates with y-up).
  // For CCW polygons, flip the edge direction (+π) so pickPositionFromSlope
  // puts labels on the exterior side of each edge.
  const isCCW = () => {
    let sum = 0;
    for (let j = 0; j < vertices.length; j++) {
      const vj = asJxgPoint(vertices[j]);
      const vnj = asJxgPoint(vertices[(j + 1) % vertices.length]);
      sum += vj.X() * vnj.Y() - vnj.X() * vj.Y();
    }
    return sum > 0;
  };

  const borderLabels: Array<import('../label/label.bind').BoundLabel> = [];
  for (let i = 0; i < borders.length; i++) {
    const borderStyle = resolveBorderStyle(theme, style?.border, style?.borderPerEdge?.[i]);
    const borderLabelConfig = resolveLabelConfig(borderStyle.label, {
      color: labelColor,
      spacing: 'compact',
      precision: ctx.defaultPrecision,
    });

    const nextI = (i + 1) % vertices.length;
    const midpoint = board.create('midpoint', [vertices[i], vertices[nextI]], {
      visible: false,
    }) as GeometryElement;
    helpers.push(midpoint);

    const typedVi = asJxgPoint(vertices[i]);
    const typedVn = asJxgPoint(vertices[nextI]);

    const boundLabel = bindLabel({
      ctx,
      themeService,
      element: midpoint,
      config: borderLabelConfig,
      contextProvider: createLineLabelContext(`${names[i]}${names[nextI]}`, borders[i]),
      slopeProvider: () => {
        const edgeAngle = Math.atan2(typedVn.Y() - typedVi.Y(), typedVn.X() - typedVi.X());
        // Flip for CCW polygons so labels appear on the outside
        return isCCW() ? edgeAngle + Math.PI : edgeAngle;
      },
    });
    borderLabels.push(boundLabel);
    attachReconfigure(borders[i], boundLabel);
  }

  // Always create polygon center label
  const polygonLabelConfig = resolveLabelConfig(style?.label, {
    color: labelColor,
    position: 'center',
    precision: ctx.defaultPrecision,
  });
  const centroid = board.create(
    'point',
    [
      () => {
        let sx = 0;
        for (const v of vertices) { sx += asJxgPoint(v).X(); }
        return sx / vertices.length;
      },
      () => {
        let sy = 0;
        for (const v of vertices) { sy += asJxgPoint(v).Y(); }
        return sy / vertices.length;
      },
    ],
    { visible: false, fixed: true, withLabel: false },
  ) as GeometryElement;
  helpers.push(centroid);
  const polygonLabel = bindLabel({
    ctx,
    themeService,
    element: centroid,
    config: polygonLabelConfig,
    contextProvider: createPolygonLabelContext(name || '', polygon),
  });
  attachReconfigure(polygon, polygonLabel);

  const result: PolygonResult = { polygon, vertices, borders, vertexLabels, borderLabels, polygonLabel, helpers };
  return result;
}
